/**
 * Per-tab active-account resolution from the URL (Gmail-style `/u/<n>/…`).
 *
 * The accounts registry (`space_accounts`) is SHARED across all tabs of the
 * same origin (it lives in localStorage). What makes two tabs show two DIFFERENT
 * accounts simultaneously is that "who is active in THIS tab" is encoded in the
 * URL path prefix `/u/<n>` — and the URL is per-tab (each browser tab is its own
 * JS realm with its own `location`). Gmail does exactly this (`/mail/u/0/…`).
 *
 * `<n>` is the 0-based INDEX into `AccountsState.accounts` (stable insertion
 * order; the explicit guest entry is an account too and also gets an index).
 *
 * Everything that used to read the GLOBAL `activeUserId` now resolves the active
 * account from the URL index instead, so it is automatically per-tab. The
 * registry's `activeUserId` is demoted to "last-used default" — it only seeds a
 * fresh tab / a bare-path redirect and NEVER overrides a tab's explicit
 * `/u/<n>`.
 *
 * CRITICAL: this reads `globalThis.location.pathname` directly (not the router)
 * so it works synchronously in every context — `getAuthToken`, the session
 * machine's module-init reconcile, the PowerSync provider's sync prime — none of
 * which can await the router.
 */

import {
  type AccountsState,
  getActiveAccount,
  type Session,
  type StoredAccount,
  VISITOR_ID,
} from "@space/shared";

/** Path prefix segment that carries the active-account index. */
const U_PREFIX = "/u";

/** localStorage key remembering the last index a tab activated (redirect default). */
export const LAST_ACTIVE_INDEX_KEY = "space_last_active_index";

const U_PATH_RE = /^\/u\/(\d+)(?=\/|$)/;

/**
 * Parses the active-account index out of a pathname (`/u/2/inbox` → `2`).
 * Returns `null` when the path is not under a `/u/<n>` prefix.
 */
export function parseActiveIndexFromPath(pathname: string): number | null {
  const match = U_PATH_RE.exec(pathname);
  if (!match?.[1]) {
    return null;
  }
  const index = Number.parseInt(match[1], 10);
  return Number.isInteger(index) && index >= 0 ? index : null;
}

/** Reads the active-account index from THIS tab's current URL. */
export function readActiveIndexFromUrl(): number | null {
  try {
    return parseActiveIndexFromPath(globalThis.location.pathname);
  } catch {
    return null;
  }
}

/** Strips a leading `/u/<n>` prefix from a path, returning the inner app path. */
export function stripUPrefix(pathname: string): string {
  const inner = pathname.replace(U_PATH_RE, "");
  return inner === "" ? "/" : inner;
}

/** Builds a full `/u/<n>` path from an index + an inner app path (default `/`). */
export function buildUPath(index: number, innerPath = "/"): string {
  const normalized = innerPath.startsWith("/") ? innerPath : `/${innerPath}`;
  // `/u/<n>` + "/" must collapse to `/u/<n>/` (the index route), not `/u/<n>//`.
  return normalized === "/" ? `${U_PREFIX}/${index}/` : `${U_PREFIX}/${index}${normalized}`;
}

/** Persists the last-activated index as the default for fresh tabs / redirects. */
export function rememberLastActiveIndex(index: number): void {
  try {
    globalThis.localStorage.setItem(LAST_ACTIVE_INDEX_KEY, String(index));
  } catch {
    // storage unavailable — best-effort only.
  }
}

/** Reads the remembered last-active index (default for a fresh tab / redirect). */
function readLastActiveIndex(): number | null {
  try {
    const raw = globalThis.localStorage.getItem(LAST_ACTIVE_INDEX_KEY);
    if (raw === null) {
      return null;
    }
    const index = Number.parseInt(raw, 10);
    return Number.isInteger(index) && index >= 0 ? index : null;
  } catch {
    return null;
  }
}

/**
 * Clamps an index into the bounds of the current accounts array. Returns `null`
 * when there are no accounts at all.
 */
function clampIndex(index: number, accountCount: number): number | null {
  if (accountCount <= 0) {
    return null;
  }
  if (index < 0) {
    return 0;
  }
  if (index >= accountCount) {
    return accountCount - 1;
  }
  return index;
}

/**
 * The index a bare path / fresh tab should redirect into:
 *   1. the remembered last-active index (clamped), else
 *   2. the registry's `activeUserId`'s index (legacy default), else
 *   3. 0.
 * Returns `null` only when there are no accounts (the guest entry will be
 * created lazily, so callers handle `null` by minting a guest at index 0).
 */
export function defaultActiveIndex(state: AccountsState): number | null {
  if (state.accounts.length === 0) {
    return null;
  }
  const remembered = readLastActiveIndex();
  if (remembered !== null) {
    return clampIndex(remembered, state.accounts.length);
  }
  const active = getActiveAccount(state);
  if (active) {
    const idx = state.accounts.findIndex((a) => a.userId === active.userId);
    if (idx !== -1) {
      return idx;
    }
  }
  return 0;
}

/**
 * Decides whether a `/u/<n>` URL index must REDIRECT to a valid one.
 *
 * The bug this guards: an out-of-range index (e.g. `/u/1` when the registry
 * only has the guest at index 0) used to be silently `clampIndex`-ed onto a
 * VALID account — rendering a "phantom" account (the guest, or the wrong real
 * account) under a URL that doesn't address it. `getAuthToken`, the session
 * view and PowerSync all key off that clamped index, so the tab would show a
 * second guest / the wrong identity instead of bouncing to a real index.
 *
 * Returns the index the route guard should redirect to, or `null` when the URL
 * index is already IN RANGE (no redirect needed). With an EMPTY registry it
 * returns `0` (the guest, minted lazily by the caller) — so a deep-link into
 * `/u/<n>` before the guest exists lands on `/u/0`, never a clamped phantom.
 *
 * Pure (the caller injects the state) so it is deterministically unit-testable;
 * the side-effects (guest mint + the actual redirect) stay in the route guard.
 */
export function outOfRangeRedirectIndex(state: AccountsState, urlIndex: number): number | null {
  // In range → the URL already addresses a real registry slot, no redirect.
  if (urlIndex >= 0 && urlIndex < state.accounts.length) {
    return null;
  }
  // Out of range (or empty registry): the nearest VALID index to land on. With
  // accounts present this is the per-tab default (last-used / active / 0); with
  // an empty registry `defaultActiveIndex` is `null`, so we target index 0 where
  // the caller mints the guest.
  return defaultActiveIndex(state) ?? 0;
}

/**
 * Resolves the account THIS tab should treat as active, from the URL index +
 * the shared registry. Falls back to the default index when the URL has no
 * `/u/<n>` (e.g. an auth page or a not-yet-redirected bare path).
 */
export function resolveTabAccount(
  state: AccountsState,
  urlIndex: number | null = readActiveIndexFromUrl(),
): StoredAccount | null {
  if (state.accounts.length === 0) {
    return null;
  }
  const index = urlIndex ?? defaultActiveIndex(state);
  if (index === null) {
    return null;
  }
  const clamped = clampIndex(index, state.accounts.length);
  return clamped === null ? null : (state.accounts[clamped] ?? null);
}

/**
 * The per-tab PowerSync DB/sync user id from the URL `/u/<n>` → registry, or
 * `null` when this tab has no account resolvable from the URL (a bare boot/auth
 * path before the `/u/<n>` redirect lands). A GUEST account resolves to
 * {@link VISITOR_ID}; a REAL account to its own `userId`.
 *
 * SINGLE SOURCE OF TRUTH for "which account does THIS tab sync": the PowerSync
 * provider uses it for BOTH the per-user DB binding AND the connect/auth gate,
 * so the tab that DISPLAYS an account is exactly the tab that SYNCS it —
 * coherent with `useSession().isAuthenticated()` (which derives from the same
 * per-tab account), never gated on only the global session machine.
 */
export function resolvePerTabSyncUserId(
  state: AccountsState,
  urlIndex: number | null,
): string | null {
  const account = resolveTabAccount(state, urlIndex);
  if (account === null) {
    return null;
  }
  return account.kind === "guest" ? VISITOR_ID : account.userId;
}

/** Returns the index of a userId in the registry, or `null` if not present. */
export function indexOfUserId(state: AccountsState, userId: string): number | null {
  const idx = state.accounts.findIndex((a) => a.userId === userId);
  return idx !== -1 ? idx : null;
}

// ─── Per-tab session VIEW (auth/role/identity gating) ───────────────────────
//
// The GLOBAL session machine holds only ONE identity (the last-authed real
// user / cookie). Gating UI on it leaks the real user's stuff onto a GUEST tab
// (admin section, sign-out, profile, /admin guard, the /login "already authed"
// guard). The fix mirrors `userId`: derive the auth/role VIEW from THIS tab's
// active account (URL `/u/<n>` → registry), NOT the global session.

/**
 * Decodes a JWT's payload claims without verifying the signature. The payload
 * is the middle base64url segment. Returns `null` for anything malformed.
 *
 * UX-ONLY: this gates which screens/links the client offers. The SERVER still
 * verifies the signature and enforces every action — a forged role claim here
 * would only reveal links that 401/403 on use.
 */
function decodeJwtClaims(token: string | null | undefined): object | null {
  if (!token) {
    return null;
  }
  const parts = token.split(".");
  if (parts.length < 2 || !parts[1]) {
    return null;
  }
  try {
    const base64 = parts[1].replaceAll("-", "+").replaceAll("_", "/");
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), "=");
    const json = globalThis.atob(padded);
    const decoded: unknown = JSON.parse(json);
    // `decoded` is narrowed to `object` by the guard — no unsafe assertion needed.
    return typeof decoded === "object" && decoded !== null ? decoded : null;
  } catch {
    return null;
  }
}

/** Extracts the `role` claim from an account's JWT (UX gating only). */
function decodeJwtRole(token: string | null | undefined): string | null {
  const claims = decodeJwtClaims(token);
  if (claims === null) {
    return null;
  }
  // `Reflect.get` reads the property off the narrowed `object` without an
  // unsafe `as Record<…>` assertion; the value is validated below.
  const role: unknown = Reflect.get(claims, "role");
  return typeof role === "string" ? role : null;
}

/** The per-tab auth/role/identity view derived from the active account. */
export interface ActiveAccountView {
  /** The resolved active account for this tab (or `null` on a bare boot path). */
  account: StoredAccount | null;
  /** True when the active account is the explicit guest (or none resolved). */
  isGuest: boolean;
  /** True only when the active account is a REAL (signed-in) account. */
  isAuthenticated: boolean;
  /** The active real account's role (from the global session or its JWT). */
  role: string | null;
  /**
   * A `Session`-shaped view of the active account for identity/role gating.
   * `undefined` for a guest. When the active account matches the global session
   * it IS the full global session (role/twoFactor/etc.); otherwise a minimal
   * view built from the StoredAccount + the JWT `role` claim.
   */
  user: Session | undefined;
}

/**
 * SINGLE SOURCE OF TRUTH for per-tab auth/role/identity gating.
 *
 * Resolves THIS tab's active account from the URL index + the shared registry,
 * then projects it into an {@link ActiveAccountView}:
 *   - GUEST (or none) → not authenticated, no user, no role.
 *   - REAL account matching the global `session` → the full global session.
 *   - REAL account NOT matching the global session (a different account open in
 *     another tab) → a minimal session view + the role decoded from its JWT.
 *
 * Pure: callers inject the state, URL index and global session so it stays free
 * of import cycles (`accounts.ts` already imports from this module).
 */
export function resolveActiveAccountView(
  state: AccountsState,
  urlIndex: number | null,
  globalSession: Session | null | undefined,
): ActiveAccountView {
  const account = resolveTabAccount(state, urlIndex);

  // Bare boot / auth path with no `/u/<n>` yet AND no registry account: fall
  // back to the global session exactly like `userId` does, so boot/auth pages
  // (which have no per-tab index) still see the real identity.
  if (account === null) {
    const fallback = globalSession ?? undefined;
    return {
      account: null,
      isGuest: !fallback,
      isAuthenticated: Boolean(fallback),
      role: fallback?.user.role ?? null,
      user: fallback,
    };
  }

  if (account.kind === "guest") {
    return { account, isGuest: true, isAuthenticated: false, role: null, user: undefined };
  }

  // Real account. Prefer the full global session when it IS this account. During
  // an in-app switch the machine is re-primed with a MINIMAL session that lacks
  // `role` (the full profile arrives later via `get-session`); fall back to the
  // account's PERSISTED role (saved at login + every get-session reflection),
  // then its JWT `role` claim, so the Role badge / admin gating doesn't blink
  // off in that window NOR when the live session was cleared by a 401.
  if (globalSession && globalSession.user.id === account.userId) {
    const role = globalSession.user.role ?? account.role ?? decodeJwtRole(account.token);
    const user =
      role === globalSession.user.role
        ? globalSession
        : { ...globalSession, user: { ...globalSession.user, role } };
    return { account, isGuest: false, isAuthenticated: true, role, user };
  }

  // Real account that is NOT the globally-primed one (another tab's account, or
  // the active account after its live session was cleared by a 401). Build a
  // minimal identity view from the PERSISTED registry profile — including the
  // persisted role (falling back to the JWT claim) so the Role badge stays
  // populated exactly like the already-persisted name/email.
  const role = account.role ?? decodeJwtRole(account.token);
  const user: Session = {
    user: {
      id: account.userId,
      email: account.email,
      name: account.displayName,
      image: account.avatar ?? null,
      role,
    },
    session: { id: "", userId: account.userId, expiresAt: "", token: account.token ?? "" },
  };
  return { account, isGuest: false, isAuthenticated: true, role, user };
}
